import * as React from 'react';
import { useTranslator } from '@bluebeam/react-i18n';
import { PublishRequest, RawPublishRequest } from 'records/PublishRequest';
import { StdListCell, StdListCheckbox } from 'components/StdList';
import AuditListRowTile from 'pages/AuditPage/AuditListRowTile.func';
import ValidatatableInput from 'components/inputs/ValidatableInput';
import BaseInput from 'components/inputs/BaseInput';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect.func';
import { SelectOption } from 'components/inputs/BaseSelect';
import DateDropdown from 'components/inputs/dropdowns/DateDropdown';
import StdListContext from 'contexts/StdListContext';

const noLeftPaddingStyle = {
  paddingLeft: '0',
};

type Props = {
  publishRequest: PublishRequest;

  isSelected: boolean;

  onSelect: (checked: boolean, pageId: string) => void;

  onChange: (key: keyof RawPublishRequest, val: string | number | null) => void;

  isDuplicateSheetNumber: boolean;

  versionNumber: number;

  sheetDisciplineOptions: SelectOption<string | null>[];

  initialSheetDisciplineSelection:
    | SelectOption<string | null>
    | undefined
    | null;
};

const AuditListRow: React.FC<Props> = React.memo((props) => {
  const {
    isSelected,
    onSelect,
    onChange,
    isDuplicateSheetNumber,
    versionNumber,
    sheetDisciplineOptions,
    initialSheetDisciplineSelection,
    publishRequest,
  } = props;

  const { translate } = useTranslator(['dropdownOptions']);

  const message = React.useMemo(() => {
    if (isDuplicateSheetNumber) {
      return 'Only one version of a drawing can be uploaded per batch. Please edit or remove.';
    }

    if (versionNumber) {
      return `Revision ${versionNumber}`;
    }

    return 'Initial Upload';
  }, [versionNumber, isDuplicateSheetNumber]);

  const backgroundColor = React.useMemo(() => {
    if (isSelected) {
      return 'rgba(var(--color-primary-rgb), 0.1)';
    }

    if (isDuplicateSheetNumber) {
      return 'rgba(222, 52, 40, 0.1)';
    }

    return 'white';
  }, [isSelected, isDuplicateSheetNumber]);

  const rowId = `audit-list-row-${publishRequest.pageid}`;

  return (
    <StdListContext.Consumer>
      {({ gridTemplateColumns }): React.ReactNode => (
        <div
          id={rowId}
          className="bgd-white bb-solid bb-width-2 bb-gray-3 br-solid br-gray-3 br-width-1"
          style={{
            backgroundColor,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns,
              gridTemplateRows: '4rem',
              alignItems: 'center',
            }}
          >
            <StdListCheckbox
              rowId={rowId}
              checked={isSelected}
              onChange={(checked) => onSelect(checked, publishRequest.pageid)}
            />
            <StdListCell id={`${rowId}-cell-tile`}>
              <AuditListRowTile publishRequest={publishRequest} />
            </StdListCell>
            <StdListCell id={`${rowId}-sheetnumber`} style={noLeftPaddingStyle}>
              <ValidatatableInput
                id={`${rowId}-sheetnumber`}
                value={publishRequest.sheetnumber}
                isValid={!isDuplicateSheetNumber}
                onChange={(evt): void =>
                  onChange('sheetnumber', evt.target.value)
                }
              />
            </StdListCell>
            <StdListCell
              id={`${rowId}-cell-sheettittle`}
              style={noLeftPaddingStyle}
            >
              <BaseInput
                id={`${rowId}-sheettitle`}
                value={publishRequest.sheettitle}
                onChange={(evt): void =>
                  onChange('sheettitle', evt.target.value)
                }
              />
            </StdListCell>
            <StdListCell
              id={`${rowId}-cell-discipline`}
              truncate={false}
              style={noLeftPaddingStyle}
            >
              <SingleSelect
                id={`${rowId}-discipline-select`}
                className="discipline-select"
                isClearable
                placeholder={translate('dropdownOptions:chooseDiscipline')}
                value={initialSheetDisciplineSelection}
                options={sheetDisciplineOptions}
                onChange={(selectedOption): void =>
                  onChange(
                    'discipline',
                    selectedOption.value ? selectedOption.value : ''
                  )
                }
              />
            </StdListCell>
            <StdListCell id={`${rowId}-issuedate`} style={noLeftPaddingStyle}>
              <DateDropdown
                id={`${rowId}-cell-issuedate`}
                date={publishRequest.issueddate}
                onChange={(date: Date): void => {
                  onChange('issueddate', date.getTime() / 1000);
                }}
              />
            </StdListCell>
            <StdListCell
              id={`${rowId}-cell-revisioncomment`}
              style={noLeftPaddingStyle}
            >
              <BaseInput
                id={`${rowId}-revisioncomment`}
                value={publishRequest.revisioncomment}
                onChange={(evt): void =>
                  onChange('revisioncomment', evt.target.value)
                }
              />
            </StdListCell>
          </div>
          <div
            className="pl8 pb2"
            style={{
              fontSize: '10px',
              color: isDuplicateSheetNumber ? '#de3428' : '#666',
            }}
          >
            {message}
          </div>
        </div>
      )}
    </StdListContext.Consumer>
  );
});

AuditListRow.displayName = 'AuditListRowFunc';

export default AuditListRow;
